import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { validateReorderLink } from "../lib/api";
import { getProductsBySubject } from "../lib/products";
import { useCart } from "../lib/cartStore";
import { SubscriptionProvider } from "../lib/subscriptionContext";
import { ProductCard } from "../components/ProductCard";
import { CartSidebar } from "../components/CartSidebar";
import { SubjectSection } from "../components/SubjectSection";
import type { SubscriptionsResponse } from "../lib/types";

type LoadingState = "loading" | "valid" | "invalid" | "error";

export function ReorderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loadingState, setLoadingState] = useState<LoadingState>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [subscriptions, setSubscriptions] = useState<SubscriptionsResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { isEmpty, entries, getTotals, setReorderContext } = useCart();

  const productsBySubject = getProductsBySubject();

  // Validate HMAC link on mount
  useEffect(() => {
    async function validateLink() {
      const school = searchParams.get("school");
      const exp = searchParams.get("exp");
      const sig = searchParams.get("sig");

      if (!school || !exp || !sig) {
        setLoadingState("invalid");
        setErrorMessage("Missing required parameters. Please use a valid reorder link.");
        return;
      }

      try {
        const result = await validateReorderLink({ school, exp, sig });

        if (!result.valid) {
          setLoadingState("invalid");
          setErrorMessage(result.reason || "This link is no longer valid.");
          return;
        }

        setSubscriptions(result.subscriptions || null);
        // Store reorder context for checkout
        if (result.subscriptions) {
          setReorderContext({
            schoolId: result.subscriptions.school_id,
            schoolName: result.subscriptions.school_name,
          });
        }
        setLoadingState("valid");
      } catch (err) {
        setLoadingState("error");
        setErrorMessage(
          err instanceof Error ? err.message : "Failed to validate link. Please try again."
        );
      }
    }

    validateLink();
  }, [searchParams, setReorderContext]);

  // Navigate to checkout page with cart data
  function handleCheckout() {
    if (isEmpty) return;

    setSubmitting(true);
    navigate("/checkout", {
      state: {
        entries,
        totals: getTotals(),
        reorder: true,
        school: subscriptions
          ? {
              acaraId: subscriptions.school_id,
              name: subscriptions.school_name,
              isManualEntry: false,
            }
          : undefined,
      },
    });
  }

  // Loading state
  if (loadingState === "loading") {
    return (
      <div className="order-page">
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Validating your link...</p>
        </div>
      </div>
    );
  }

  // Invalid or error state
  if (loadingState === "invalid" || loadingState === "error") {
    return (
      <div className="order-page">
        <header className="order-header">
          <h1>Link Invalid</h1>
        </header>
        <div className="error-state">
          <div className="error-icon">!</div>
          <h2>{loadingState === "invalid" ? "Link Expired or Invalid" : "Something Went Wrong"}</h2>
          <p>{errorMessage}</p>
          <div className="error-actions">
            <Link to="/" className="btn btn-primary">
              Start a new order
            </Link>
            <a href="mailto:support@smartermaths.com.au" className="btn btn-secondary">
              Contact support
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Valid state - show reorder form
  // Wrap with SubscriptionProvider so ProductCards can detect owned products
  const subscriptionsList = subscriptions?.subscriptions || [];

  return (
    <SubscriptionProvider subscriptions={subscriptionsList}>
      <div className="order-page">
        <header className="order-header">
          <img src="https://smartered.wpengine.com/wp-content/uploads/2023/01/SmarterEd-logo.svg" alt="SmarterEd" className="order-header-logo" />
          <div className="order-header-text">
            <h1>Welcome Back</h1>
            <p>
              {subscriptions?.school_name
                ? `Manage subscriptions for ${subscriptions.school_name}`
                : "Add products or additional seats to your subscriptions"}
            </p>
          </div>
        </header>

        <div className="order-layout">
          <main className="catalog-section">
            {/* Product catalog - owned products shown with green styling */}
            {Array.from(productsBySubject.entries()).map(([subject, products]) => (
              <SubjectSection key={subject.id} subject={subject} productIds={products.map((group) => group.id)}>
                <div className="product-grid">
                  {products.map((group) => (
                    <ProductCard key={group.id} group={group} />
                  ))}
                </div>
              </SubjectSection>
            ))}
          </main>

          <aside className="cart-section">
            <CartSidebar
              onSubmit={handleCheckout}
              onCancel={() => {}}
              submitting={submitting}
              isPunchOut={false}
            />
          </aside>
        </div>
      </div>
    </SubscriptionProvider>
  );
}
